import moment from 'moment';
import convertUnits from 'convert-units';
import { growl, warn } from '@ifit/verbose-ui';
import { getApiClient, getGatewayClient } from '../../client';
import * as localhostData from './local-data';
import { shouldUseLocalData } from '../../util/local';
const details = 'DASHBOARD::DETAILS';
const deviceType = 'DASHBOARD::DEVICE_TYPE';
const equipmentType = 'DASHBOARD::EQUIPMENT_TYPE';
const items = 'DASHBOARD::ITEMS';
const isTransition = 'DASHBOARD::IS_TRANSITION';
const loading = 'DASHBOARD::LOADING';
const softwareNumber = 'DASHBOARD::SOFTWARE_NUMBER';
const validWOTDEquipmentType = ['cycle', 'run', 'fusion'];

function removeFavorites(workouts, deviceType) {
  if (deviceType !== 'mobile') return workouts;
  return workouts.filter((workout) => {
    return ((workout.type || '').toLowerCase() !== 'navigation' || (workout.navigation_type || '').toLowerCase() !== 'favorites');
  });
}

function getCardData() {
  if (shouldUseLocalData()) {
    return (dispatch) => {
      dispatch(setItems(mergeToFeaturedContent(localhostData.dashboardItems, localhostData.workoutEstimatedData)));
      return Promise.resolve();
    };
  }

  return (dispatch, getState) => {
    const { softwareNumber, equipmentType, deviceType } = getState().dashboard;
    const handleErrors = (err) => {
      dispatch(setItems(null));
      const message = err || 'getCardData Error';
      warn(message);
      return Promise.reject(message);
    };
    const apiClient = getApiClient();
    if (!apiClient) return handleErrors('API client is not created');
    const gatewayClient = getGatewayClient();
    if (!gatewayClient) return handleErrors('Gateway client is not created');
    let featuredContent;

    return getFeaturedContent(equipmentType, softwareNumber)
      .then((results) => {
        featuredContent = results;
        const workoutIds = featuredContent
          .filter(fc => fc.workout_id)
          .map(fc => fc.workout_id);
        return getWorkoutEstimatedData(workoutIds, softwareNumber);
      })
      .then((estimates) => {
        const items = mergeToFeaturedContent(removeFavorites(featuredContent, deviceType), estimates);
        dispatch(setItems(items));
      })
      .catch(handleErrors);
  };
}

function getFeaturedContent(equipmentType, softwareNumber) {
  return Promise.all([
    getWOTDFeaturedContent(equipmentType, softwareNumber),
    getEquipmentFeaturedContent(equipmentType, softwareNumber)
  ])
    .then(results => [results[0], ...results[1]].filter(fc => fc));
}

function getWOTDFeaturedContent(equipmentType, softwareNumber) {
  if (!equipmentType || validWOTDEquipmentType.indexOf(equipmentType) === -1) {
    return Promise.resolve(null);
  }
  return getApiClient().WorkoutOfTheDay.get(equipmentType, softwareNumber || 0)
    .then((wotd) => {
      if (wotd && wotd.data && wotd.data.workout_id) {
        return getApiClient().Workout.get(wotd.data.workout_id)
          .then(workout => mapWorkoutToCardData(workout.data));
      }
      return null;
    })
    .catch((e) => {
      if (e && e.response && (e.response.status === 404 || e.response.status === 400)) {
        /* istanbul ignore next */
        growl(`no WorkoutOfTheDay defined for an equipmentType of ${equipmentType} and softwareNumber of ${softwareNumber}.  Add some in admin.`); // eslint-disable-line
      } else {
        warn(e);
      }
      return null;
    });
}

function getEquipmentFeaturedContent(equipmentType, softwareNumber) {
  return getApiClient().EquipmentFeaturedContent.get(softwareNumber || 0, equipmentType || 'all')
    .then(efc => efc.data)
    .catch((e) => {
      if (e && e.response && (e.response.status === 404 || e.response.status === 400)) {
        /* istanbul ignore next */
        growl(`no EquipmentFeaturedContent defined for an equipmentType of ${equipmentType} and softwareNumber of ${softwareNumber}.  Add some in admin.`); // eslint-disable-line
      } else {
        warn(e);
      }
      return [];
    });
}

function mapWorkoutToCardData(workout) {
  if (!workout) {
    return null;
  }
  return {
    id: 'wotd',
    workout_id: workout.workout_id,
    title: workout.title,
    type: workout.type,
    dashboard_label: workout.dashboard_label,
    image_url: workout.image_uri
  };
}

function mergeToFeaturedContent(featuredContent, estimates) {
  const estimatesLookupTable = estimates
    .filter(e => e.workoutId)
    .reduce((map, estimate) => {
      map[estimate.workoutId] = estimate;
      return map;
    }, {});

  return featuredContent.map((fc) => {
    const estimate = estimatesLookupTable[fc.workout_id];
    if (!estimate) return fc;
    const { seconds, meters, calories } = estimate;
    const time = seconds ? moment.utc(seconds * 1e3).format('H:mm') : undefined;
    const distance = meters ? `${convertUnits(meters).from('m').to('mi').toFixed(2)} mi` : undefined;
    const caloriesFormatted = calories ? `${calories} cals` : undefined;

    return {
      ...fc,
      details: {
        ...fc.details,
        ...{
          cal: caloriesFormatted,
          distance,
          time
        }
      }
    };
  });
}

function getWorkoutEstimatedData(workoutIds, softwareNumber) {
  return getGatewayClient().WorkoutEstimator.estimate({ workoutIds, softwareNumber })
    .then(results => results || []);
}

function setDetails(value) {
  return {
    type: details,
    value
  };
}

function setDeviceType(value) {
  return {
    type: deviceType,
    value
  };
}

function setEquipmentType(value) {
  return {
    type: equipmentType,
    value
  };
}

function setIsTransition(value) {
  return {
    type: isTransition,
    value
  };
}

function setSoftwareNumber(value) {
  return {
    type: softwareNumber,
    value
  };
}

function setItems(value) {
  return {
    type: items,
    value
  };
}

export default {
  details,
  deviceType,
  equipmentType,
  items,
  isTransition,
  loading,
  softwareNumber,
  getCardData,
  setDetails,
  setDeviceType,
  setEquipmentType,
  setItems,
  setIsTransition,
  setSoftwareNumber,
  mapWorkoutToCardData
};


// WEBPACK FOOTER //
// ./src/views/dashboard/actions.js